
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const pricingTiers = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Basic fraud detection for individual users",
    features: [
      "Text analysis (5 per month)",
      "Basic image verification",
      "Results history (7 days)",
      "Community support",
    ],
    cta: "Get Started",
    color: "bg-gray-100 dark:bg-fraudeye-primary/70",
    href: "/register",
  },
  {
    name: "Pro",
    price: "$19",
    period: "per month",
    description: "Advanced analysis for professionals",
    features: [
      "Unlimited text analysis",
      "Full image verification",
      "Document authenticity checks",
      "Video deepfake detection",
      "Voice analysis",
      "Results history (30 days)",
      "Email support",
    ],
    cta: "Subscribe Now",
    color: "bg-fraudeye-accent/10 dark:bg-fraudeye-accent/20",
    highlight: true,
    href: "/register?plan=pro",
  },
  {
    name: "Business",
    price: "$49",
    period: "per month",
    description: "Enterprise-grade protection for teams",
    features: [
      "Everything in Pro",
      "Team member accounts",
      "API access",
      "Priority analysis queue",
      "Custom detection models",
      "Unlimited history",
      "24/7 priority support",
    ],
    cta: "Contact Sales",
    color: "bg-gray-100 dark:bg-fraudeye-primary/70",
    href: "/register?plan=business",
  },
];

const Pricing = () => {
  const { user } = useAuth();
  
  return (
    <section className="py-16 bg-white dark:bg-fraudeye-primary" id="pricing">
      <div className="container px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight">Transparent Pricing</h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Choose the plan that's right for you - whether you're an individual, a professional, or a business.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingTiers.map((tier, index) => (
            <div
              key={index}
              className={`${tier.color} rounded-xl p-6 border shadow-sm transition-all duration-300 hover:shadow-md ${
                tier.highlight ? "ring-2 ring-fraudeye-accent scale-105 md:scale-105" : ""
              }`}
            >
              <div className="mb-4">
                <h3 className="text-xl font-bold">{tier.name}</h3>
                <div className="mt-2 flex items-baseline">
                  <span className="text-3xl font-extrabold">{tier.price}</span>
                  <span className="ml-1 text-sm text-muted-foreground">{tier.period}</span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{tier.description}</p>
              </div>
              
              <ul className="mt-6 space-y-3">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="h-5 w-5 text-fraudeye-accent shrink-0 mr-2" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8">
                <Button
                  asChild
                  className={`w-full ${
                    tier.highlight
                      ? "bg-fraudeye-accent hover:bg-fraudeye-highlight"
                      : ""
                  }`}
                  variant={tier.highlight ? "default" : "outline"}
                >
                  <Link to={user ? "/dashboard" : tier.href}>
                    {tier.cta}
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            Need a custom plan for your organization?{" "}
            <Link to="/contact" className="text-fraudeye-accent hover:text-fraudeye-highlight underline">
              Contact us
            </Link>{" "}
            for enterprise pricing.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
